import { ChangeEvent, ComponentState, useCallback, useContext, useState } from "react";
import { CustomCellEditorProps } from "ag-grid-react";
import { format } from "date-fns";
import Calendar from "composants/calendar/Calendar";
import CalendarHours from "composants/calendar/CalendarHours";
import CalendarHoursSecondes from "composants/calendar/CalendarHoursSecondes";
import { WviContext } from "../DatatableContext";
import { getOptionsByType } from "utils/component.utils";
import { DataInteractionContext } from "hooks/useInteractions";
import { Pojo } from "types/Galaxy";
import { flatDataFromPopjo } from "../AgGridContainer";
import { CalendarEditorVariant, editorFormats, toLocalDate } from "../utils/dateUtils";
import { convertValue } from "utils/entities.utils";

export interface CellEditorCalendarProps {
  ctrlKey: string;
  componentID: string;
  compo: ComponentState;
  columnIndex: number;
  variant: CalendarEditorVariant;
}

const calendarComponents = {
  date: Calendar,
  dateTime: CalendarHours,
  dateTimeSeconds: CalendarHoursSecondes,
} as const;

function toEditorInputValue(value: unknown, editorFormat: string): string {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  const date = toLocalDate(value);
  return date ? format(date, editorFormat) : "";
}

export function buildCellEditorCalendar({
  ctrlKey,
  componentID,
  columnIndex,
  compo,
  variant,
}: CellEditorCalendarProps) {
  const CalendarComponent = calendarComponents[variant];
  const editorFormat = editorFormats[variant];

  return ({ value, data, colDef, rowIndex, onValueChange }: CustomCellEditorProps) => {
    const wviState = useContext(WviContext);
    const wviStateCol = colDef.colId ? wviState?.[data.id]?.[colDef.colId]?.type : undefined;

    const navigationParams = {
      "data-navigation-key": ctrlKey,
      "data-navigation-row": rowIndex,
      "data-navigation-column": columnIndex,
    };
    const options = getOptionsByType(compo);

    // État local en chaîne : évite la réinitialisation du champ lors de la saisie clavier
    // (année, etc.) quand AG Grid renvoie la valeur convertie via valueGetter.
    const [localInputValue, setLocalInputValue] = useState(() =>
      toEditorInputValue(value, editorFormat),
    );

    const onChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        setLocalInputValue(input.value);

        if (!input.validity.valid) {
          return;
        }

        const value = convertValue(e);
        onValueChange(value, e);
      },
      [onValueChange],
    );

    return (
      <DataInteractionContext.Provider value={flatDataFromPopjo(data) as Pojo}>
        <CalendarComponent
          autoFocus={true}
          id={componentID}
          name={compo.column}
          value={localInputValue}
          onChange={onChange}
          onContextMenu={() => {}}
          readOnly={compo.isReadonly}
          disabled={compo.isDisabled}
          style={{ width: "100%" }}
          wviState={wviStateCol}
          {...navigationParams}
          {...options}
        />
      </DataInteractionContext.Provider>
    );
  };
}
